import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Regras e dados da escolha da mensagem aprovada (Douglas 25/09/2026) — usados
// pela Nova conversa e pelo quadro da janela fechada (EscolhaMensagemAprovada).
// A intenção vem do nome de exibição "Intenção · Apelido", gravado pelo sync
// (sincronizar-templates-oficiais.service.ts) a partir do catálogo do backend.

export type MensagemAprovada = {
  id: string;
  template_name: string;
  nome_exibicao: string;
  corpo: string;
  variaveis: Array<{ pos: number; campo: string }> | null;
  categoria: string | null;
  language: string | null;
  status_meta: string | null;
  ativo: boolean | null;
  componentes_raw: unknown;
};

export const ORDEM_INTENCAO = ['Abertura', 'Retorno', 'Ligação', 'Novidade', 'Proposta', 'Novo número', 'Encerramento'];
// Lista fixa: texto livre no assunto faz a Meta tratar a mensagem como marketing.
export const ASSUNTOS = [
  'sua cotação', 'seu pedido', 'suas linhas', 'sua fatura', 'a portabilidade', 'a instalação', 'seu atendimento',
];

export function intencaoDe(t: { nome_exibicao: string | null }): { intencao: string; apelido: string } | null {
  const [intencao, apelido] = String(t.nome_exibicao || '').split(' · ');
  return apelido && ORDEM_INTENCAO.includes(intencao) ? { intencao, apelido } : null;
}

export const liberado = (t: MensagemAprovada) => !!t.ativo && t.status_meta === 'approved';
export const ehMarketing = (t: MensagemAprovada) => String(t.categoria || '').toUpperCase() === 'MARKETING';

export function motivoBloqueio(t: MensagemAprovada): string {
  if (t.status_meta === 'rejected') return 'Recusada pela Meta';
  if (t.status_meta === 'paused' || t.status_meta === 'disabled') return 'Pausada pela Meta';
  return 'Em análise na Meta — libera sozinha quando aprovar';
}

/** As mensagens da linha: liberadas e as do catálogo ainda bloqueadas. */
export function useMensagensAprovadas(connectionId: string | null | undefined, enabled = true) {
  return useQuery<MensagemAprovada[]>({
    queryKey: ['mensagens-aprovadas', connectionId],
    enabled: enabled && !!connectionId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('id, template_name, nome_exibicao, corpo, variaveis, categoria, language, status_meta, ativo, componentes_raw')
        .eq('connection_id', connectionId!)
        .neq('status_meta', 'deleted')
        .order('nome_exibicao');
      if (error) throw error;
      // Só as Uti (Douglas 26/09/2026: "poucas e custo baixo"). Mensagem que a
      // Meta deixou em Mkt nesta linha entra como Uti quando a cópia "<nome>_uti"
      // já foi aprovada aqui: o envio troca pela cópia (bee360-enviar.service.ts).
      const todas = (data || []) as MensagemAprovada[];
      const copiaUti = new Set(todas
        .filter((t) => t.template_name.endsWith('_uti') && t.status_meta === 'approved' && !ehMarketing(t))
        .map((t) => t.template_name.slice(0, -4)));
      return todas
        .filter((t) => !t.template_name.endsWith('_uti'))
        .filter((t) => liberado(t) || !!intencaoDe(t))
        .filter((t) => !ehMarketing(t) || copiaUti.has(t.template_name))
        .map((t) => (ehMarketing(t) ? { ...t, categoria: 'UTILITY' } : t));
    },
  });
}

const EMPRESA = /\b(LTDA|EIRELI|ME|EPP|S\/?A|COMERCIO|COMÉRCIO|SERVICOS|SERVIÇOS|INDUSTRIA|INDÚSTRIA)\b|\d/i;
export function primeiroNome(nome: string | null | undefined): string {
  const n = String(nome || '').trim();
  if (!n || EMPRESA.test(n)) return '';
  const p = n.split(/\s+/)[0];
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
}

/** Primeiro nome do contato real do CNPJ (o mesmo "Contato real" do Painel CRM). */
export function useContatoDoCliente(cnpj: string | null | undefined) {
  const limpo = String(cnpj || '').replace(/\D/g, '');
  return useQuery<string | null>({
    queryKey: ['mensagens-aprovadas-contato', limpo],
    enabled: limpo.length === 14,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('cnpj_contato_master')
        .select('contato_real_nome, gestor_nome')
        .eq('cnpj', limpo)
        .maybeSingle();
      const d = data as { contato_real_nome?: string | null; gestor_nome?: string | null } | null;
      return d?.contato_real_nome || d?.gestor_nome || null;
    },
  });
}

const cnpjBonito = (c: string) => {
  const d = c.replace(/\D/g, '');
  return d.length === 14 ? d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : c;
};

// O campo pode vir com o nome antigo (contato_nome, consultor_nome, razao_social)
// até o sync regravar a linha — os dois nomes valem.
export function tipoDoCampo(campo: string): 'cliente' | 'consultor' | 'empresa' | 'cnpj' | 'assunto' | null {
  const c = campo.toLowerCase();
  if (c === 'cliente' || c === 'contato_nome') return 'cliente';
  if (c === 'consultor' || c === 'consultor_nome') return 'consultor';
  if (c === 'empresa' || c === 'razao_social') return 'empresa';
  if (c === 'cnpj') return 'cnpj';
  if (c === 'assunto') return 'assunto';
  return null;
}

export function valoresIniciais(
  t: MensagemAprovada,
  ctx: { cliente?: string; consultor?: string; empresa?: string; cnpj?: string },
): Record<number, string> {
  const out: Record<number, string> = {};
  for (const v of t.variaveis || []) {
    const tipo = tipoDoCampo(v.campo);
    const valor = tipo === 'cliente' ? ctx.cliente
      : tipo === 'consultor' ? ctx.consultor
        : tipo === 'empresa' ? ctx.empresa
          : tipo === 'cnpj' ? (ctx.cnpj ? cnpjBonito(ctx.cnpj) : '')
            : tipo === 'assunto' ? ASSUNTOS[0] : '';
    if (valor) out[v.pos] = valor;
  }
  return out;
}

export function renderizarCorpo(corpo: string, valores: Record<number, string>): string {
  return corpo.replace(/\{\{(\d+)\}\}/g, (m, n) => valores[Number(n)] || m);
}

export function botoesDe(t: MensagemAprovada): string[] {
  const comps = Array.isArray(t.componentes_raw) ? t.componentes_raw as Array<Record<string, unknown>> : [];
  const bloco = comps.find((c) => String(c.type).toUpperCase() === 'BUTTONS');
  const lista = Array.isArray(bloco?.buttons) ? bloco!.buttons as Array<{ text?: string }> : [];
  return lista.map((b) => String(b.text || '')).filter(Boolean);
}

export const rotuloCampo = (campo: string) => {
  const tipo = tipoDoCampo(campo);
  return tipo ?? campo.replace(/_/g, ' ');
};

export function custoDaMensagem(t: MensagemAprovada | null): string {
  if (!t) return '';
  return ehMarketing(t) ? 'Mkt · R$ 0,34 por envio' : 'R$ 0,04 · de graça se o cliente escreveu nas últimas 24h';
}
